import type * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn.ts';

export interface WeekNavProps {
  label: string;
  onPrevious: () => void;
  onNext: () => void;
  previousLabel: string;
  nextLabel: string;
}

export function WeekNav({
  label,
  onPrevious,
  onNext,
  previousLabel,
  nextLabel,
}: WeekNavProps) {
  return (
    <div data-slot='week-nav' className='mb-4 flex items-center justify-center gap-4'>
      <WeekNavButton label={previousLabel} onClick={onPrevious}>
        <ChevronLeft className='size-4' aria-hidden='true' />
      </WeekNavButton>
      <span className='min-w-35 text-center text-md font-bold text-ink'>{label}</span>
      <WeekNavButton label={nextLabel} onClick={onNext}>
        <ChevronRight className='size-4' aria-hidden='true' />
      </WeekNavButton>
    </div>
  );
}

function WeekNavButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type='button'
      title={label}
      aria-label={label}
      onClick={onClick}
      className='flex size-8 cursor-pointer items-center justify-center rounded-full border border-line bg-surface text-ink-muted transition hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint'
    >
      {children}
    </button>
  );
}

export interface WeekGridProps extends Omit<React.ComponentProps<'div'>, 'style'> {
  /* Weekend columns are a personal setting, so the track count is a prop. */
  columns: number;
}

/*
 * The week, one column per day.
 *
 * Below 900px the columns stop shrinking and the grid scrolls sideways
 * instead. Five columns divided across a phone gives ~67px each, which fits
 * neither a name nor a time — a week you cannot read is worse than a week you
 * have to swipe. The count is a custom property so one class can express both
 * layouts.
 */
export function WeekGrid({ columns, className, ...props }: WeekGridProps) {
  return (
    <div
      data-slot='week-grid'
      className={cn(
        'grid items-start gap-2.5',
        'grid-cols-[repeat(var(--week-columns),minmax(0,1fr))]',
        'max-agenda:grid-cols-[repeat(var(--week-columns),minmax(108px,1fr))]',
        'max-agenda:overflow-x-auto max-agenda:pb-2',
        className,
      )}
      style={{ '--week-columns': columns } as React.CSSProperties}
      {...props}
    />
  );
}

export interface WeekColumnHeadProps {
  /* Short weekday name, e.g. "Ma". */
  day: string;
  /* Day of the month. */
  date: string;
  today?: boolean;
}

/*
 * "MA 15" on one baseline, and a surface only on today.
 *
 * A filled block on every day draws five headers where the week has one thing
 * to say — which day you are in. The columns are already told apart by the
 * cards under them, so the head carries the date and nothing else, and the navy
 * pill is left to mean the one thing it should. Both days keep the same padding
 * so today's pill adds a surface, never a row of extra height.
 */
export function WeekColumnHead({ day, date, today = false }: WeekColumnHeadProps) {
  return (
    <div
      data-slot='week-column-head'
      className={cn(
        'mb-2.5 flex items-baseline justify-center gap-1.5 rounded-sm px-1 py-1.75',
        today && 'bg-primary ring-1 ring-selected-edge',
      )}
    >
      <span
        className={cn(
          'text-xs font-bold tracking-wide uppercase',
          today ? 'text-white/70' : 'text-ink-muted',
        )}
      >
        {day}
      </span>
      <span className={cn('text-sm font-bold', today ? 'text-white' : 'text-ink')}>
        {date}
      </span>
    </div>
  );
}

export type WeekColumnProps = Omit<React.ComponentProps<'div'>, 'style'>;

export function WeekColumn({ className, ...props }: WeekColumnProps) {
  return (
    <div
      data-slot='week-column'
      className={cn('flex min-h-15 flex-col gap-2', className)}
      {...props}
    />
  );
}

export type WeekEmptyProps = Omit<React.ComponentProps<'div'>, 'style'>;

export function WeekEmpty({ className, ...props }: WeekEmptyProps) {
  return (
    <div
      data-slot='week-empty'
      className={cn('px-1 py-3.5 text-center text-xs text-ink-muted', className)}
      {...props}
    />
  );
}

/**
 * What a meeting on the agenda is to Wundo, which is also what a click on it
 * does.
 *
 * `tracked` — Wundo is in this conversation; the click opens it.
 * `candidate` — it is on the calendar only; the click adds it.
 * `inert` — nothing to converse about, so nothing to click. An event with
 * nobody else on it is on the week to show the week, and no more.
 */
export type MeetingState = 'tracked' | 'candidate' | 'inert';

/*
 * A meeting in the week view.
 *
 * No coloured side bars and no status text: the week grid answers "what is on
 * this week", and decoration that needs a legend to read is decoration the
 * cards did not earn.
 *
 * The surface carries the state and the pill says what the click does. White
 * with a border is in Wundo, grey is not — the same stacking and the same sizes
 * either way, so the difference runs over ink and surface rather than over
 * weight, and adding a meeting simply turns its card white.
 */
export const meetingCardVariants = cva(
  [
    'group relative block w-full rounded-md border p-3 pt-2.5 pb-2.25 text-left transition',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
  ],
  {
    variants: {
      state: {
        tracked: 'cursor-pointer border-line bg-surface hover:shadow-hover',
        candidate: 'cursor-pointer border-transparent bg-fill hover:bg-line',
        inert: 'border-transparent bg-fill',
      },
    },
    defaultVariants: {
      state: 'tracked',
    },
  },
);

export type MeetingCardVariants = VariantProps<typeof meetingCardVariants>;

export interface MeetingCardProps extends Omit<
  React.ComponentProps<'button'>,
  'style' | 'title' | 'children'
> {
  title: string;
  /* Start time and duration, already formatted, e.g. "10:00 · 30 min". */
  time: string;
  /* The kind pill, or the "+ Wundo" pill on a card that is not in Wundo yet. */
  badge?: React.ReactNode;
  /* A control of its own in the badge's place, e.g. an `AddPillButton`; the rest of the card keeps the card's click. */
  action?: React.ReactNode;
  state?: MeetingState;
}

export function MeetingCard({
  title,
  time,
  badge,
  action,
  state = 'tracked',
  className,
  ...props
}: MeetingCardProps) {
  /* Lifted above the stretched button, and only as wide as the control, so the rest of the row still opens the card. */
  const pill = action ? (
    <span className='relative z-1 flex min-w-0'>{action}</span>
  ) : (
    badge
  );
  const body = (
    <>
      <span
        className={cn(
          /* A calendar title is somebody else's text: one long token breaks rather than runs out of the card. */
          'block text-sm leading-tight font-bold break-words',
          state === 'tracked' ? 'text-ink' : 'text-ink-muted',
        )}
      >
        {title}
      </span>
      <span className='mt-0.75 block text-xs font-semibold text-ink-muted'>{time}</span>
      {/* The container a pill measures to know whether its full label fits; see `PillLabel`. */}
      {pill && <span className='mt-1.25 flex @container/pill'>{pill}</span>}
    </>
  );

  /*
   * A card with nothing to do is not a button. Leaving it one would put it in
   * the tab order and promise an action on Enter that never comes; the button
   * props go with it, because there is nothing left for them to describe.
   */
  if (state === 'inert') {
    return (
      <div
        data-slot='meeting-card'
        data-state={state}
        className={cn(meetingCardVariants({ state }), className)}
      >
        {body}
      </div>
    );
  }

  /*
   * A control inside a button is neither allowed nor announced. So a card with
   * one of its own gets its click from a button stretched over it, and the
   * control sits on top, next in the tab order.
   */
  if (action) {
    return (
      <div
        data-slot='meeting-card'
        data-state={state}
        className={cn(meetingCardVariants({ state }), className)}
      >
        <button
          data-slot='meeting-card-open'
          type='button'
          className='absolute inset-0 cursor-pointer rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint'
          {...props}
        />
        {body}
      </div>
    );
  }

  return (
    <button
      data-slot='meeting-card'
      data-state={state}
      type='button'
      className={cn(meetingCardVariants({ state }), className)}
      {...props}
    >
      {body}
    </button>
  );
}
